import BlockIcon from "@mui/icons-material/Block";
import { Box, Grid, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CustomSlider from "../../../SegmentBuilder/Shared/Slider";
import ColorSwatch from "../../../Swatch";
import Tabs from "../../../Tabs";
// import ToolAccordion from "../../../SegmentBuilder/Shared/ToolAccordion";
// import { ASSETS } from "../../../../utils/config";
import { useDispatch, useSelector } from "react-redux";
import { gradients } from "../../../../constants/background";
import { setBgWallpaper } from "../../../../redux/actions/wheelWallpaper";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { hexToRgb } from "../../../../utils/helpers";
import CustomAccordion from "../../../Accordion";
import SegmentColor from "../../../SegmentSettings/children/SegmentColor";
import ImageView from "./children/Image";
import * as Styled from "./Wallpaper.styled";
import {
  patterns,
  StyledColorTypeSelector,
  StyledEmptyPattern,
  StyledPatternContainer,
  StyledRightIconContainer,
} from "./Wallpaper.styled"; // New file for additional styled components
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import UploadedImage from "../../Main/PortraitTab/UploadedImage";
import WallpaperImageSettings from "../../../WallpaperImageSettings";
import ImageCropper from "../../../ImageCropper";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import { FEATURES } from "../../../../constants/features";
import TierFeatureBadge from "../../../TierFeatureBadge";
import AutoScrollAccordion from "../AutoScrollAccordion";
// Self-contained SVG Patterns module (parametric checkerboard, dots, stripes,
// grid, zigzag, chevron … with scale + recolor). Rendered as a permanent
// "SVG Patterns" category in the wallpaper picker.
import { BackgroundPatternPanel } from "../../../backgroundPatterns";
import { BG_PATTERNS_ENABLED } from "../../../backgroundPatterns/featureFlag";
import {
  PROCEDURAL_LIBRARY_ENABLED,
  DesignPanel,
  LoopPanel,
  PresetPanel,
} from "../../../proceduralLibrary";
import GradientBuilder from "../../../gradientBuilder";
import {
  isGradientWallpaper,
  isGradientSwatchSelected,
  isSegmentBackgroundCleared,
  CLEAR_GRADIENT_VALUE,
  resolveOriginalImage,
} from "./wallpaperValue";
import { shouldSeedPatternColor } from "./patternColorInit";
import {
  SWATCH_FOCUS_RING_SX,
  getSwatchA11yProps,
  getSwatchGroupProps,
} from "./swatchA11y";
import WALLPAPER_TAB_PROPS from "./wallpaperTabProps";

const Wallpaper = ({
  title = "Wallpaper",
  background = "#ffffff",
  // segment-environments:F8.2 (2026-08-06): slider1* was ALWAYS accepted as a prop by every
  // caller of this component but never destructured/rendered anywhere below — silently dropped.
  // `isShowResizeSlider` defaults FALSE (opposite default from `isShowTransparencySlider`,
  // deliberately): only ONE current caller (SegmentSettings/index.jsx) wires real slider1Value/
  // slider1Change handlers; the OTHER call site (SegmentEnvironment.jsx) never passes them at
  // all, so a default-true render there would mount a slider bound to undefined value/onChange
  // and throw the moment a user dragged it. Defaulting false keeps every existing caller
  // byte-identical; the one caller that wants it opts in explicitly.
  slider1Value,
  slider1Change,
  slider1BoxTitle,
  isShowResizeSlider = false,
  slider2Value,
  slider2Change,
  slider2BoxTitle,
  handleWallpaperOverlayColorChange,
  wallpaperOverlayColor,
  handleSelectWallpaper,
  bgWallpaper,
  handleSelectPattern,
  isPattern,
  handleSelectColorType,
  handlePatternColorChange,
  handlePatternColorInitialChange,
  selectedColorType,
  patternColor,
  onBgWallpaperColorChange,
  currentBg,
  segmentBgColor,
  showAccordion = true,
  handleGradientColor,
  patternId,
  isShowTransparencySlider = true,
  handleSelectBackgroundResizeMode,
  resizeMode,
  customCategories = ["Colors", "Gradient", "Patterns", "Image"],
  showSuggestedColorPalette = true,
  showEmbedURLDisclaimer = false,
  cropAspectRatio = 16 / 9,
  huePercentage = 0,
  huePercentageChange,
  hueColor = "#ffffff",
  hueColorChange,
}) => {
  const dispatch = useDispatch();
  // The procedural media libraries ride on the existing `handleGradientColor` →
  // bg_wallpaper slot (patterns/designs/loops, currentBg 3) or the image path
  // (presets, currentBg 2), so they need no new redux wiring. Each category is
  // additive and flag-gated: OFF = the picker behaves exactly as before.
  const categories = [
    ...customCategories,
    ...(BG_PATTERNS_ENABLED ? ["SVG Patterns"] : []),
    ...(PROCEDURAL_LIBRARY_ENABLED
      ? ["Loops", "Designs", "Preset wallpapers"]
      : []),
  ];
  const [selectedCategory, setSelectedCategory] = useState(customCategories[0]);
  const { getPaletteColor } = usePaletteColor();
  const [openCropper, setOpenCropper] = useState(false);
  const [originalImage, setOriginalImage] = useState(
    bgWallpaper?.src || bgWallpaper || null,
  ); // New state for original image

  // Seed originalImage when a croppable bgWallpaper arrives by non-cropper means
  // (e.g. persisted value hydrates after mount, or a different segment is
  // selected). See resolveOriginalImage — the old `!setOriginalImage` guard
  // tested the state setter (always truthy), so this branch was dead (F6.11).
  useEffect(() => {
    setOriginalImage((prev) => resolveOriginalImage(bgWallpaper, prev));
  }, [bgWallpaper]);

  // Handler for initial image selection (from Preset, Upload, Browse, Recent)
  const handleSelectInitialWallpaper = (value) => {
    handleSelectWallpaper(value);
    setOriginalImage(value); // Sets original for cropping
  };

  // Renders the flag-gated procedural library categories. Returns null for any
  // non-procedural category so the host cascade can fall through to its own tabs.
  const renderProceduralCategory = (cat) => {
    const segColor = getPaletteColor
      ? getPaletteColor(segmentBgColor)
      : undefined;
    switch (cat) {
      case "SVG Patterns":
        return (
          <BackgroundPatternPanel
            value={bgWallpaper}
            onApply={handleGradientColor}
            segmentColor={segColor}
          />
        );
      case "Loops":
        return (
          <LoopPanel onApply={handleGradientColor} segmentColor={segColor} />
        );
      case "Designs":
        return (
          <DesignPanel
            value={bgWallpaper}
            onApply={handleGradientColor}
            segmentColor={segColor}
          />
        );
      case "Preset wallpapers":
        return <PresetPanel onSelect={handleSelectInitialWallpaper} />;
      default:
        return null;
    }
  };

  const PROCEDURAL_CATEGORIES = [
    "SVG Patterns",
    "Loops",
    "Designs",
    "Preset wallpapers",
  ];

  const getWallpaperBgColor = useCallback((color) => {
    const [r, g, b] = hexToRgb(color);
    return `rgba(${r},${g},${b},0.5)`;
  }, []);

  const { isEligible, subscription, requiredSubscription } = useIfUserEligible(
    FEATURES.WHEEL_WALLPAPER_IMAGE
  );
  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    if (newValue === "Patterns") {
      handleSelectPattern(patternId ? patternId : 0);
      // handlePatternColorChange(patternColor ? patternColor : selectedSegmentColor);
      // handleWallpaperOverlayColorChange(wallpaperOverlayColor);
    }
  };

  const categoriesWithIcons = categories.map((category) => {
    const config = {
      label: category,
      value: category,
    };

    // Add tooltip only to Image category when not eligible. Tier copy/icon are
    // DERIVED from the entitlement rather than assumed.
    if (category === "Image" && !isEligible) {
      config.customIcon = (
        <TierFeatureBadge
          requiredSubscription={requiredSubscription}
          subscription={subscription}
          placement="bottom"
          size="20px"
        />
      );
    }

    return config;
  });

  const RightIconComponent = () => {
    return (
      <StyledRightIconContainer>
        {currentBg === 0 || currentBg === 3 ? (
          <StyledRightIconContainer bgColor={getPaletteColor(segmentBgColor)} />
        ) : currentBg === 1 ? (
          <StyledRightIconContainer bgColor={wallpaperOverlayColor}>
            {patterns.find((pattern) => pattern.id === patternId)?.component &&
              React.createElement(
                patterns.find((pattern) => pattern.id === patternId).component,
                {
                  color: getPaletteColor(patternColor),
                },
              )}
          </StyledRightIconContainer>
        ) : bgWallpaper ? (
          <Styled.SwatchOverlayImage
            overlayColor={getWallpaperBgColor(wallpaperOverlayColor)}
            isPattern={isPattern}
          >
            <Styled.SwatchBackgroundImage src={bgWallpaper} alt="Wallpaper" />
          </Styled.SwatchOverlayImage>
        ) : (
          <BlockIcon />
        )}
      </StyledRightIconContainer>
    );
  };

  const ColorTypeSelector = ({
    onClick,
    isSelected,
    wallpaperOverlayColor,
    bgWallpaper,
    patternColor,
    marginTop,
    colorType,
  }) => {
    return (
      <StyledColorTypeSelector
        onClick={onClick}
        isSelected={isSelected}
        wallpaperOverlayColor={wallpaperOverlayColor}
      >
        <div style={{ marginTop }}>
          {colorType === 2 &&
            patternId > 0 &&
            patterns.find((pattern) => pattern.id === patternId)?.component &&
            React.createElement(
              patterns.find((pattern) => pattern.id === patternId).component,
              {
                color: getPaletteColor(patternColor),
              },
            )}
        </div>
      </StyledColorTypeSelector>
    );
  };

  const renderColorsTab = useCallback(() => {
    const cleared = isSegmentBackgroundCleared(segmentBgColor);
    return (
      <>
        {/* F3.7: a per-Segment background could be SET from this tab but never
            UNSET — the Gradient tab has a clear tile, Colors had none, so once a
            creator picked a solid colour the Segment was stuck with a background
            for good. Same canonical cleared value the Gradient clear writes, so
            "no background" is ONE state however it was reached. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, pt: 1 }}>
          <Box
            role="button"
            tabIndex={0}
            aria-label="No background color"
            aria-pressed={cleared}
            onClick={() => handleGradientColor(CLEAR_GRADIENT_VALUE)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                e.preventDefault();
                handleGradientColor(CLEAR_GRADIENT_VALUE);
              }
            }}
            sx={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: "50%",
              border: cleared ? "2px solid #BBBBBB" : "2px solid transparent",
              "&:focus-visible": {
                outline: "2px solid #81459B",
                outlineOffset: "2px",
              },
            }}
          >
            <BlockIcon />
          </Box>
          <Typography
            sx={{ fontSize: "12px", color: "#667085", fontFamily: "DM Sans" }}
          >
            No background
          </Typography>
        </Stack>
        <SegmentColor
          isExpanded={true}
          showTabs={false}
          // The Colors tab WRITES `segmentBgColor` (onBgWallpaperColorChange ->
          // handleChange("segmentBgColor")), so its "currently selected" swatch must
          // READ the same field. It used to read `wallpaperOverlayColor` — an
          // unrelated pattern-overlay value — so on every (re)mount and on every
          // segment switch SegmentColor re-seeded its highlight from the wrong
          // field: reopening the pane highlighted the wrong swatch (or none), and
          // switching segments left the previous segment's highlight in place.
          // Same defect class as the gradient highlight fixed in #2273 (F4.3).
          currentColor={getPaletteColor(segmentBgColor)}
          title={"Color"}
          onColorChange={onBgWallpaperColorChange}
          wallpaperOverlayColor={getWallpaperBgColor(wallpaperOverlayColor)}
          showSuggestedColorPalette={showSuggestedColorPalette}
        />
      </>
    );
  }, [
    getWallpaperBgColor,
    getPaletteColor,
    onBgWallpaperColorChange,
    segmentBgColor,
    wallpaperOverlayColor,
    showSuggestedColorPalette,
    handleGradientColor,
  ]);

  const selectedSegment = useSelector(getSelectedSegment);

  // `handlePatternColorInitialChange` is an OPTIONAL prop: the per-Segment caller
  // (SegmentSettings) supplies it, the wheel-level caller (Main/WallpaperTab) does
  // not. Calling it unconditionally when `patternColor` was undefined threw
  // "handlePatternColorInitialChange is not a function" and unmounted the whole
  // Design > Wallpaper pane for any wheel whose `wallpaper_settings` was null.
  // The slice-level fix (redux/reducers/wheelWallpaper) stops `patternColor` from
  // being undefined in the first place; this guard is the defense-in-depth half so
  // an absent optional callback can never be invoked again. See patternColorInit.
  useEffect(() => {
    if (shouldSeedPatternColor(patternColor, handlePatternColorInitialChange)) {
      handlePatternColorInitialChange("suggested_color_0");
    }
  }, [selectedSegment, patternColor, handlePatternColorInitialChange]);
  const renderPatternsTab = () => {
    const selectedPattern = patterns?.find(
      (pattern) => pattern?.id === patternId,
    );
    const PatternComponent = selectedPattern ? selectedPattern.component : null;

    return (
      <>
        {currentBg === 1 && patternId >= 1 && PatternComponent ? (
          <>
            <Styled.OverlayContainer bgColor={wallpaperOverlayColor}>
              <div style={{ marginLeft: "-10px" }}>
                {PatternComponent &&
                  React.createElement(PatternComponent, {
                    color: getPaletteColor(patternColor),
                  })}
              </div>
            </Styled.OverlayContainer>
            <Stack
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              mt={2}
            >
              <ColorTypeSelector
                onClick={() => handleSelectColorType(0)}
                isSelected={false}
                wallpaperOverlayColor={"white"}
                patternColor={"black"}
                marginTop={"0"}
                colorType={2}
              />

              <ColorTypeSelector
                onClick={() => handleSelectColorType(1)}
                isSelected={selectedColorType === 1}
                wallpaperOverlayColor={wallpaperOverlayColor}
                patternColor={getPaletteColor(patternColor)}
                marginTop={"0"}
                colorType={1}
              />
              <ColorTypeSelector
                onClick={() => handleSelectColorType(2)}
                isSelected={selectedColorType === 2}
                wallpaperOverlayColor={
                  patternColor !== undefined
                    ? getPaletteColor(patternColor)
                    : "suggested_color_0"
                }
                patternColor={getPaletteColor(patternColor)}
                colorType={1}
              />
            </Stack>
          </>
        ) : null}

        {(selectedColorType === 0 || selectedColorType == null) && (
          <Styled.GridContainer {...getSwatchGroupProps("Background patterns")}>
            {patterns.map((pattern, index) =>
              pattern.id === 0 ? (
                <StyledEmptyPattern
                  key={index}
                  onClick={() => {
                    handleSelectPattern(pattern?.id);
                  }}
                  isSelected={patternId === 0}
                  {...getSwatchA11yProps({
                    label: "No pattern",
                    selected: patternId === 0,
                    onActivate: () => handleSelectPattern(pattern?.id),
                  })}
                >
                  <BlockIcon style={{ color: "#d5d9e0", fontSize: "40px" }} />
                </StyledEmptyPattern>
              ) : (
                <StyledPatternContainer
                  key={index}
                  onClick={() => {
                    handleSelectPattern(pattern?.id);
                  }}
                  isSelected={patternId === pattern.id}
                  {...getSwatchA11yProps({
                    label: `Pattern ${pattern.id}`,
                    selected: patternId === pattern.id,
                    onActivate: () => handleSelectPattern(pattern?.id),
                  })}
                >
                  {pattern.component &&
                    React.createElement(pattern.component, {
                      color: "black",
                    })}
                </StyledPatternContainer>
              ),
            )}
          </Styled.GridContainer>
        )}

        {[1, 2].includes(selectedColorType) && (
          <SegmentColor
            showTabs={false}
            currentColor={wallpaperOverlayColor}
            title={"Color"}
            onColorChange={
              selectedColorType === 2
                ? handlePatternColorChange
                : handleWallpaperOverlayColorChange
            }
            wallpaperOverlayColor={getWallpaperBgColor(wallpaperOverlayColor)}
            showSuggestedColorPalette={showSuggestedColorPalette}
          />
        )}
      </>
    );
  };

  return (
    <>
      {showAccordion ? (
        <CustomAccordion
          title={title}
          icon={<RightIconComponent />}
          onChange={() => {}}
        >
          {/* <ToolAccordion
          accordionIcon={ASSETS.segmentEditor.ContainerWallpaper}
          title={title}
          AccordionSummaryStyle={{ p: 0, m: 0 }}
          RightIconComponent={RightIconComponent}
        > */}

          <Styled.Container>
            <Tabs
              categoriesWithIcons={categoriesWithIcons.filter(
                (e) => e.value !== "Patterns",
              )}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              color={"#81459B"}
              indicatorColor="#C283DA"
              {...WALLPAPER_TAB_PROPS}
            />
            {selectedCategory === "Colors" ? (
              renderColorsTab()
            ) : selectedCategory === "Image" ? (
              <>
                {bgWallpaper &&
                  !isGradientWallpaper(bgWallpaper) &&
                  currentBg == 2 && (
                    <UploadedImage
                      handleRemoveImage={() => handleSelectWallpaper("")}
                      image={bgWallpaper?.src || bgWallpaper}
                      setOpen={setOpenCropper}
                    />
                  )}

                <ImageView
                  handleRemoveImage={() => handleSelectWallpaper("")}
                  handleSelectWallpaper={handleSelectInitialWallpaper}
                  handleEditImage={(value) => {
                    handleSelectWallpaper(value);
                    setOriginalImage(value);
                  }}
                  bgWallpaper={bgWallpaper}
                  isPattern={isPattern}
                  currentBg={currentBg}
                  resizeMode={resizeMode}
                  handleSelectBackgroundResizeMode={
                    handleSelectBackgroundResizeMode
                  }
                  showPreview={false}
                />
                {bgWallpaper &&
                  !isGradientWallpaper(bgWallpaper) &&
                  currentBg == 2 && (
                    <AutoScrollAccordion
                      title="Image Tint"
                      RightIconComponent={() => (
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            backgroundColor: hueColor,
                            borderRadius: "50%",
                            border: "1px solid #a2a2a2",
                          }}
                        ></div>
                      )}
                    >
                      <WallpaperImageSettings
                        huePercentage={huePercentage}
                        huePercentageChange={huePercentageChange}
                        hueColor={hueColor}
                        hueColorChange={hueColorChange}
                      />
                    </AutoScrollAccordion>
                  )}
              </>
            ) : selectedCategory === "Patterns" ? (
              renderPatternsTab()
            ) : PROCEDURAL_CATEGORIES.includes(selectedCategory) ? (
              renderProceduralCategory(selectedCategory)
            ) : selectedCategory === "Gradient" ? (
              <Box>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    paddingLeft: "8px",
                    marginBottom: "8px",
                    fontFamily: "DM Sans",
                  }}
                >
                  Preset
                </Typography>
                <Grid
                  container
                  spacing={1}
                  alignItems="center"
                  {...getSwatchGroupProps("Gradient presets")}
                >
                  <Grid item>
                    <Box
                      onClick={() => handleGradientColor(CLEAR_GRADIENT_VALUE)}
                      {...getSwatchA11yProps({
                        label: "No gradient",
                        selected: isGradientSwatchSelected(
                          segmentBgColor,
                          CLEAR_GRADIENT_VALUE,
                        ),
                        onActivate: () =>
                          handleGradientColor(CLEAR_GRADIENT_VALUE),
                      })}
                      sx={{
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "50%",
                        outline: "none",
                        "&:focus-visible": {
                          outline: "2px solid #81459B",
                          outlineOffset: "2px",
                        },
                      }}
                    >
                      <BlockIcon />
                    </Box>
                  </Grid>
                  {gradients?.map((color, index) => (
                    <Grid item key={color}>
                      <Box
                        onClick={() => handleGradientColor(color)}
                        sx={SWATCH_FOCUS_RING_SX}
                        {...getSwatchA11yProps({
                          label: `Gradient ${index + 1}`,
                          selected: isGradientSwatchSelected(
                            segmentBgColor,
                            color,
                          ),
                          onActivate: () => handleGradientColor(color),
                        })}
                      >
                        <ColorSwatch
                          color={color}
                          borderColor={
                            isGradientSwatchSelected(segmentBgColor, color) &&
                            "#BBBBBB"
                          }
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <GradientBuilder
                  value={background}
                  onApply={handleGradientColor}
                />
              </Box>
            ) : null}
            {/* segment-environments:F8.2 (2026-08-06): slider1 (Resizing) was accepted as a
                prop but never rendered anywhere in this component — fixed by mirroring the
                existing slider2 (Opacity) block below. */}
            {isShowResizeSlider && (
              <Stack mx={1}>
                <CustomSlider
                  title={slider1BoxTitle}
                  value={slider1Value}
                  onChange={slider1Change}
                />
              </Stack>
            )}
            {isShowTransparencySlider && (
              <Stack mx={1}>
                <CustomSlider
                  title={slider2BoxTitle}
                  value={slider2Value}
                  onChange={slider2Change}
                  filledColor="#F79A00"
                  thumbColor="#F79A00"
                  bgColor="rgba(254, 235, 213, 0.9)"
                />
              </Stack>
            )}
          </Styled.Container>
        </CustomAccordion>
      ) : (
        <Styled.Container>
          <Tabs
            categoriesWithIcons={categoriesWithIcons.filter(
              (e) => e.value !== "Patterns",
            )}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            color={"#81459B"}
            indicatorColor="#C283DA"
            {...WALLPAPER_TAB_PROPS}
          />
          {bgWallpaper &&
            !isGradientWallpaper(bgWallpaper) &&
            currentBg == 2 && (
              <UploadedImage
                handleRemoveImage={() => dispatch(setBgWallpaper("", 0))}
                image={bgWallpaper?.src || bgWallpaper}
                setOpen={setOpenCropper}
              />
            )}

          {selectedCategory === "Colors" ? (
            renderColorsTab()
          ) : selectedCategory === "Image" ? (
            <>
              <ImageView
                handleRemoveImage={() => dispatch(setBgWallpaper("", 0))}
                handleSelectWallpaper={handleSelectInitialWallpaper}
                handleEditImage={(value) => {
                  dispatch(setBgWallpaper(value, 0));
                  setOriginalImage(value);
                }}
                bgWallpaper={bgWallpaper}
                isPattern={isPattern}
                currentBg={currentBg}
                showPreview={false}
                showEmbedURLDisclaimer={showEmbedURLDisclaimer}
                segmentColor={
                  getPaletteColor ? getPaletteColor(segmentBgColor) : undefined
                }
              />

              {bgWallpaper &&
                !isGradientWallpaper(bgWallpaper) &&
                currentBg == 2 && (
                  <AutoScrollAccordion
                    title="Image Tint"
                    RightIconComponent={() => (
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: hueColor,
                          borderRadius: "50%",
                          border: "1px solid #a2a2a2",
                        }}
                      ></div>
                    )}
                  >
                    <WallpaperImageSettings
                      huePercentage={huePercentage}
                      huePercentageChange={huePercentageChange}
                      hueColor={hueColor}
                      hueColorChange={hueColorChange}
                    />
                  </AutoScrollAccordion>
                )}
            </>
          ) : selectedCategory === "Patterns" ? (
            renderPatternsTab()
          ) : PROCEDURAL_CATEGORIES.includes(selectedCategory) ? (
            // Same cascade as the accordion branch — a category tab must never
            // exist without a render case (validator EW: 3 dead tabs shipped
            // when only "SVG Patterns" was handled here).
            renderProceduralCategory(selectedCategory)
          ) : selectedCategory === "Gradient" ? (
            <Box>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  paddingLeft: "8px",
                  marginBottom: "8px",
                  fontFamily: "DM Sans",
                }}
              >
                Preset
              </Typography>
              <Grid
                container
                spacing={1}
                alignItems="center"
                {...getSwatchGroupProps("Gradient presets")}
              >
                <Grid item>
                  <Box
                    onClick={() => handleGradientColor(CLEAR_GRADIENT_VALUE)}
                    {...getSwatchA11yProps({
                      label: "No gradient",
                      selected: isGradientSwatchSelected(
                        segmentBgColor,
                        CLEAR_GRADIENT_VALUE,
                      ),
                      onActivate: () =>
                        handleGradientColor(CLEAR_GRADIENT_VALUE),
                    })}
                    sx={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderRadius: "50%",
                      outline: "none",
                      "&:focus-visible": {
                        outline: "2px solid #81459B",
                        outlineOffset: "2px",
                      },
                    }}
                  >
                    <BlockIcon />
                  </Box>
                </Grid>
                {gradients?.map((color, index) => (
                  <Grid item key={color}>
                    <Box
                      onClick={() => handleGradientColor(color)}
                      sx={SWATCH_FOCUS_RING_SX}
                      {...getSwatchA11yProps({
                        label: `Gradient ${index + 1}`,
                        selected: isGradientSwatchSelected(
                          segmentBgColor,
                          color,
                        ),
                        onActivate: () => handleGradientColor(color),
                      })}
                    >
                      <ColorSwatch
                        color={color}
                        borderColor={
                          isGradientSwatchSelected(segmentBgColor, color) &&
                          "#BBBBBB"
                        }
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <GradientBuilder
                value={background}
                onApply={handleGradientColor}
              />
            </Box>
          ) : null}
          {/* segment-environments:F8.2 (2026-08-06): same fix as the accordion=true block
              above, mirrored for the accordion=false (tabs) layout. */}
          {isShowResizeSlider && (
            <Stack mx={1}>
              <CustomSlider
                title={slider1BoxTitle}
                value={slider1Value}
                onChange={slider1Change}
              />
            </Stack>
          )}
          {isShowTransparencySlider && (
            <Stack mx={1}>
              <CustomSlider
                title={slider2BoxTitle}
                value={slider2Value}
                onChange={slider2Change}
                filledColor="#F79A00"
                thumbColor="#F79A00"
                bgColor="rgba(254, 235, 213, 0.9)"
              />
            </Stack>
          )}
        </Styled.Container>
      )}

      <ImageCropper
        src={originalImage}
        open={openCropper}
        handleClose={() => setOpenCropper(false)}
        handleEditImage={handleSelectWallpaper}
        lockedAspectRatio={cropAspectRatio}
      />
    </>
  );
};

export default Wallpaper;
